import { useMemo, useState } from 'react';

import { useLazyQuery } from '@apollo/client';
import { convertsCentsToCurrency } from '@npi/buddy-utils';
import { Hierarchy } from '@prisma/client';
import dayjs from 'dayjs';

import { useParams } from '@redwoodjs/router';
import { useQuery } from '@redwoodjs/web';

import { Flex } from 'src/components/Flex';
import Loader from 'src/components/Loader/Loader';
import { showErrorToast } from 'src/components/Toasts/toast';
import ViewPDFModal from 'src/components/ViewPDFModal';
import { BillingDetails } from 'src/features/finance/sepaDirectDebits/components';
import {
    GET_CREDIT_NOTE_ITEM,
    GET_CREDIT_NOTE_PDF,
} from 'src/graphql/queries/getCreditNoteItem.query';
import { PageContent } from 'src/layouts/MainLayout/PageContent';
import { FormatBillingDetails, FormatMerchantBillingDetails } from 'src/lib/formatters';
import findBillingContact from 'src/utils/findBillingContact';
import { onError } from 'src/utils/utils';

import {
    CallToActions,
    CreditNoteDetails,
    CreditNoteLineItems,
    CreditNoteReason,
    TotalAmounts,
} from './components';

const ViewCreditNotePage = () => {
    const { id } = useParams();
    const [pdfFile, setPDFFile] = useState<File>();
    const [fileUrl, setFileUrl] = useState<string>();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const { data, loading } = useQuery(GET_CREDIT_NOTE_ITEM, {
        onError,
        variables: { id },
    });

    const { recipientName, billingInformation, creditNote } = useMemo(() => {
        const to = data?.creditNoteItem?.invoiceToType ?? Hierarchy.PARTNER;
        if (to === Hierarchy.PARTNER) {
            return {
                creditNote: data?.creditNoteItem ?? {},
                recipientName: `${
                    data?.creditNoteItem?.partner?.primaryContactInformation?.pci_firstName ?? ''
                } ${data?.creditNoteItem?.partner?.primaryContactInformation?.pci_lastName ?? ''}`,
                billingInformation: FormatBillingDetails(
                    data?.creditNoteItem?.partner?.billingContactInformation
                ),
            };
        }
        const billingContactDetails = findBillingContact(
            data?.creditNoteItem?.merchant?.application?.merchantInformation?.principals
        );
        return {
            creditNote: data?.creditNoteItem ?? {},
            recipientName: `${billingContactDetails?.firstName ?? ''} ${
                billingContactDetails?.lastName ?? ''
            }`,
            billingInformation: FormatMerchantBillingDetails(
                data?.creditNoteItem?.merchant?.application?.merchantInformation
            ),
        };
    }, [data]);

    const [getCreditNotePDF] = useLazyQuery(GET_CREDIT_NOTE_PDF, {
        onError,
        onCompleted: (data) => {
            const file = new File([data.viewPDFCreditNote.previewUrl], 'credit_Note.pdf', {
                type: 'application/pdf',
            });

            try {
                setPDFFile(file);
                setFileUrl(data.viewPDFCreditNote.previewUrl);
                setIsModalOpen(true);
            } catch (error) {
                showErrorToast(error);
            }
        },
    });

    const onClosePdfModal = () => {
        setIsModalOpen(false);
        setPDFFile(undefined);
        setFileUrl(undefined);
    };

    return (
        <>
            {loading ? (
                <Loader />
            ) : (
                <PageContent
                    heading="View Credit Note"
                    callToActions={
                        <CallToActions
                            billingContactEmailAddress={billingInformation?.email}
                            creditNote={creditNote}
                            onView={async () => {
                                try {
                                    await getCreditNotePDF({
                                        variables: {
                                            sageCreditNoteId: creditNote?.sageCreditNoteId,
                                        },
                                    });
                                } catch (error) {
                                    showErrorToast(
                                        error.message ??
                                            'An error occurred retrieving the invoice from Sage'
                                    );
                                }
                            }}
                        />
                    }
                >
                    <Flex flexDirection="column" gap="24px">
                        <Flex gap="inherit">
                            <CreditNoteDetails
                                invoiceId={creditNote?.salesInvoice?.sageInvoiceNumber}
                                invoiceDate={dayjs(creditNote?.createdAt).toDate()}
                                creditNumber={creditNote?.id}
                                creditStatus={creditNote.status}
                                recipientName={recipientName}
                                recipientType={creditNote.invoiceToType}
                            />
                            <Flex flexDirection="column" flex={1.5} gap="24px">
                                <BillingDetails
                                    billingAddress={billingInformation.billingAddress}
                                    contactNumber={billingInformation.contactNumber}
                                    name={billingInformation.billingContact}
                                    email={billingInformation.email}
                                />
                                <TotalAmounts
                                    totalCreditAmount={convertsCentsToCurrency(
                                        creditNote?.totalCreditAmount
                                    )}
                                    totalNetAmount={convertsCentsToCurrency(
                                        creditNote?.totalCreditNetAmount
                                    )}
                                    totalVatAmount={convertsCentsToCurrency(
                                        creditNote?.totalCreditVatAmount
                                    )}
                                />
                            </Flex>
                        </Flex>
                        <CreditNoteLineItems lineItems={creditNote?.creditNoteItems} />
                        <CreditNoteReason reasons={creditNote.reason} />
                    </Flex>
                </PageContent>
            )}

            {pdfFile && (
                <ViewPDFModal
                    title="View Credit Note PDF"
                    file={pdfFile}
                    fileURL={fileUrl}
                    isModalOpen={isModalOpen}
                    setIsModalOpen={onClosePdfModal}
                    maxWidth="lg"
                    dialogStylesSX={{ padding: '8px' }}
                />
            )}
        </>
    );
};
export default ViewCreditNotePage;
